// Pure helpers extracted from `GanttChart.js` (v222).
//
// All functions in this module are side-effect-free, framework-free,
// and depend only on their arguments. They power the chart's derived
// state (month bands, break overlays, phase boundaries, etc.) but no
// longer carry any closure dependency on component-level refs.
//
// Why extract these?
//   1. Shrinks GanttChart.js so the JSX-heavy component is easier to
//      read and refactor further.
//   2. Each helper is now unit-testable in isolation (no React render).
//   3. New SOW/Compare/Quote views can reuse the exact same date-math
//      without forking copies.

// ── Date math primitives ───────────────────────────────────────────────
const MS_PER_DAY = 1000 * 60 * 60 * 24;

function _parseISO(d) {
  if (!d) return null;
  const x = new Date(d + 'T00:00:00');
  return isNaN(x.getTime()) ? null : x;
}

// Week index (1-based) for a given date relative to a project start date.
function _weekIndex(date, base) {
  return Math.floor((date - base) / MS_PER_DAY / 7) + 1;
}

// ── 1. phaseBoundaries ────────────────────────────────────────────────
//
// Build a clean, deduped list of `{phase, start_week, end_week}` rows
// suitable for the "Lock to phase: <Phase>" context-menu items. Phases
// from the planner's `phase_schedule` are passed in independently of
// the active gantt view.
export function buildPhaseBoundaries(phaseSchedule) {
  if (!Array.isArray(phaseSchedule) || phaseSchedule.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const p of phaseSchedule) {
    const key = `${p.phase}|${p.start_week}|${p.end_week}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (p.phase && p.start_week && p.end_week) {
      out.push({ phase: p.phase, start_week: p.start_week, end_week: p.end_week });
    }
  }
  return out;
}

// ── 2. monthBands ─────────────────────────────────────────────────────
//
// When `startDate` is provided, group consecutive weeks that fall
// within the same calendar month into a single header cell. Gives
// stakeholders a calendar context above the weekly grid without
// hiding the weeks. Returns null when no startDate so the caller can
// fall back to a weeks-only header.
export function buildMonthBands(startDate, maxWeek) {
  const base = _parseISO(startDate);
  if (!base) return null;
  const segs = [];
  for (let w = 0; w < maxWeek; w++) {
    const d = new Date(base);
    d.setDate(d.getDate() + w * 7);
    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    const last = segs[segs.length - 1];
    if (last && last.key === key) last.span += 1;
    else segs.push({ key, label, span: 1 });
  }
  return segs;
}

// ── 3. todayWeek ──────────────────────────────────────────────────────
//
// Vertical-rule marker week — the week that contains today's date.
// Returns null when startDate is missing/invalid or when today falls
// outside the project horizon.
export function buildTodayWeek(startDate, maxWeek) {
  const base = _parseISO(startDate);
  if (!base) return null;
  const w = _weekIndex(new Date(), base);
  if (w < 1 || w > maxWeek) return null;
  return w;
}

// ── 3b. projectProgress ──────────────────────────────────────────────
//
// Status chip data for the Gantt header. Has three shapes so the UI
// can switch its look without re-doing the math:
//   - { state: 'before', weeksUntilStart, startLabel }   → "Starts in 18w"
//   - { state: 'during', currentWeek, totalWeeks,
//       weeksRemaining, pctComplete }                    → "T18 / 59w · 41w left"
//   - { state: 'after',  endedAgoWeeks }                 → "Completed"
// Returns null when `startDate` is missing or invalid (no chip shown).
export function buildProjectProgress(startDate, totalWeeks) {
  const base = _parseISO(startDate);
  if (!base || !totalWeeks || totalWeeks <= 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysSinceStart = Math.floor((today - base) / MS_PER_DAY);
  const startLabel = base.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (daysSinceStart < 0) {
    const weeksUntilStart = Math.ceil(-daysSinceStart / 7);
    return { state: 'before', weeksUntilStart, startLabel };
  }
  const currentWeek = Math.floor(daysSinceStart / 7) + 1;
  if (currentWeek > totalWeeks) {
    const endedAgoWeeks = currentWeek - totalWeeks;
    return { state: 'after', endedAgoWeeks };
  }
  const weeksRemaining = Math.max(0, totalWeeks - currentWeek);
  const pctComplete = Math.min(100, Math.round((currentWeek / totalWeeks) * 100));
  return { state: 'during', currentWeek, totalWeeks, weeksRemaining, pctComplete };
}

// ── 4. releaseMarkers ─────────────────────────────────────────────────
//
// Public Cloud release events that fall within the project horizon.
// Each entry gets `week` so the Gantt can render a vertical rule plus
// a colored chip — PMs avoid scheduling go-lives inside maintenance
// windows or land just before a release.
export function buildReleaseMarkers(startDate, releases, maxWeek) {
  const base = _parseISO(startDate);
  if (!base || !Array.isArray(releases) || releases.length === 0) return [];
  const out = [];
  for (const evt of releases) {
    const d = _parseISO(evt?.date);
    if (!d) continue;
    const w = _weekIndex(d, base);
    if (w < 1 || w > maxWeek) continue;
    out.push({ ...evt, week: w });
  }
  return out;
}

// ── 5. conditionOverlays ──────────────────────────────────────────────
//
// Convert ISO date ranges in `conditions.breaks` / `conditions.blackouts`
// to `{start_week, end_week, label, id}` so they render as absolute-
// positioned bars in each schedule row's lane. Skips silently when
// `startDate` is unknown or invalid.
export function buildConditionOverlays(conditions, startDate, maxWeek) {
  if (!conditions || !startDate) return { breaks: [], blackouts: [] };
  const base = _parseISO(startDate);
  if (!base) return { breaks: [], blackouts: [] };
  const toWeekRange = (item) => {
    if (!item || !item.start_date || !item.end_date) return null;
    const s = _parseISO(item.start_date);
    const e = _parseISO(item.end_date);
    if (!s || !e) return null;
    const sw = Math.max(1, _weekIndex(s, base));
    const ew = Math.min(maxWeek, _weekIndex(e, base));
    if (ew < 1 || sw > maxWeek || ew < sw) return null;
    return { start_week: sw, end_week: ew, label: item.label || '', id: item.id };
  };
  return {
    breaks: (conditions.breaks || []).map(toWeekRange).filter(Boolean),
    blackouts: (conditions.blackouts || []).map(toWeekRange).filter(Boolean),
  };
}

// ── 6. componentBreakRanges ──────────────────────────────────────────
//
// For each break with `applies_to='all'|'specific'`, resolve which
// component names honor it. Returns a small lookup helper plus a
// resolver function so the caller can ask `getBreaksFor('LMS Config')`
// without re-walking the breaks list per component.
//
// Returns `{ getBreaksForComponent }`.
export function buildComponentBreakResolver(conditions, conditionOverlays) {
  const breakRangesById = {};
  (conditionOverlays?.breaks || []).forEach((b) => { breakRangesById[b.id] = b; });
  const breakDefs = (conditions?.breaks || []);

  const getBreaksForComponent = (compName) => {
    const result = [];
    breakDefs.forEach((def) => {
      const range = breakRangesById[def.id];
      if (!range || !def) return;
      const applies = (def.applies_to || 'all').toLowerCase();
      if (applies === 'none') return;
      if (applies === 'specific' && !(def.component_names || []).includes(compName)) return;
      result.push({ ...range, label: def.label || range.label || 'Break' });
    });
    return result;
  };
  return { getBreaksForComponent };
}

// ── 7. splitSegmentsAroundBreaks ─────────────────────────────────────
//
// Subtract break week-ranges from a component's [start, end] range,
// returning the surviving segments. A break that splits the bar yields
// two segments; non-overlapping breaks are skipped.
export function splitSegmentsAroundBreaks(startWeek, endWeek, breaks) {
  let segments = [[startWeek, endWeek]];
  for (const br of breaks) {
    const next = [];
    for (const [s, e] of segments) {
      if (br.end_week < s || br.start_week > e) {
        next.push([s, e]);
        continue;
      }
      if (br.start_week > s) next.push([s, br.start_week - 1]);
      if (br.end_week < e) next.push([br.end_week + 1, e]);
    }
    segments = next;
  }
  return segments;
}
